import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Chip, Button, IconButton, Grid, TextField, Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab,
    Menu, MenuItem, Collapse
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt, MdDeleteOutline } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import AddLeads from "./AddLeads";
import EditLeads from "./EditLeads";
import ViewLeads from "./ViewLeads";
import DeleteLeads from "./DeleteLeads";
import { useLazyGetLeadsListQuery, useLazyGetExportLeadsQuery } from "store/slices/apis/leadsApiSlice";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalenderSVG from "assets/logo/Calendar";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";
import { exportToExcel } from "utils/ExportToExcel";
import { usePermission } from "hooks/usePermission";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { useDispatch } from "store";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import TrackerPageHeader from "components/TrackerPageHeader";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import { LEADS_TRACKER_CREATE, LEADS_TRACKER_DELETE, LEADS_TRACKER_EDIT, LEADS_TRACKER_VIEW } from "helpers/permissionConfig";

const normalize = (value) =>
    (value || "")
        .toString()
        .trim()
        .toUpperCase();

// const statusOptions = [
//     { label: "Warm", value: "Warm" },
//     { label: "Hot", value: "Hot" },
//     { label: "Cold", value: "Cold" },
// ];

// const typeOptions = [
//     { label: "BTS", value: "BTS" },
//     { label: "RTM", value: "RTM" },
//     { label: "Speculative", value: "Speculative" },
// ];

const totalCount = 0;
const openCount = 0;
const closedCount = 0;

const LeadsTracker = () => {

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const createAccess = usePermission(LEADS_TRACKER_CREATE);
    const editAccess = usePermission(LEADS_TRACKER_EDIT);
    const viewAccess = usePermission(LEADS_TRACKER_VIEW);
    const deleteAccess = usePermission(LEADS_TRACKER_DELETE);

    const [searchTerm, setSearchTerm] = useState("");

    const { projectId } = useParams();
    const { data: leadsData } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });


    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditLeads(menuRow);
        }
        handleMenuClose();
    };


    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('CreatedDate');

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddLeads, setIsAddLeads] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [deleteData, setDeleteData] = useState(null);

    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: "",
    });

    const module = "Leads";

    const { data: leadsStatusRes } = useGetMasterDataQuery({ module: module, category: 'Status', ProjectId: projectId });
    const statusData = leadsStatusRes?.data || [];
    const statusOptions = mapAndSortAlphabetically(statusData);


    const { data: leadsCurrentStageRes } = useGetMasterDataQuery({ module: module, category: 'CurrentStage', ProjectId: projectId });
    const currentStageData = leadsCurrentStageRes?.data || [];
    const currentStageOptions = mapAndSortAlphabetically(currentStageData);

    const { data: leadsTypeRes } = useGetMasterDataQuery({ module: module, category: 'Type', ProjectId: projectId });
    const typeData = leadsTypeRes?.data || [];
    const typeOptions = mapAndSortAlphabetically(typeData);

    const { data: proposalStatusRes } = useGetMasterDataQuery({ module: module, category: 'ProposalStatus', ProjectId: projectId });
    const proposalStatusData = proposalStatusRes?.data || [];
    const proposalStatusOptions = mapAndSortAlphabetically(proposalStatusData);

    const dispatch = useDispatch();
    const [trigger, { data: leadsList, isFetching }] = useLazyGetLeadsListQuery();
    const [exportTrigger, { isFetching: isExporting }] = useLazyGetExportLeadsQuery();

    const ALL_EXPORT_COLUMNS = [
        { name: "ID", field: "customId" },
        { name: "Name", field: "name" },
        { name: "Asset Name", field: "assetName" },
        { name: "Asset Type", field: "assetType" },
        { name: "Area In Sqft", field: "area" },
        { name: "Expectation Rental Rate", field: "expectationRentalRate" },
        { name: "Status", field: "status" },
        { name: "Type", field: "type" },
        { name: "Site Visit Date", field: "siteVisitDate" },
        { name: "Start Date", field: "startDate" },
        { name: "Source", field: "source" },
        { name: "Proposal Date", field: "proposalDate" },
        { name: "Proposal Status", field: "proposalStatus" },
        { name: "Proposed Rental Base", field: "proposedRentalBase" },
        { name: "Proposed Rental CAM", field: "proposedRentalCam" },
        { name: "Proposed Rental TI", field: "proposedRentalTi" },
        { name: "Current Stage", field: "currentStage" },
        { name: "LOI Date", field: "loiDate" },
        { name: "Time To Deal", field: "timeToDeal" },
        { name: "Last Action", field: "lastAction" },
        { name: "Next Steps", field: "nextSteps" },
    ];

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const handleExportExcel = async () => {
        try {
            const exportFilters = {
                ...filters,
                PageNumber: 1,
                PageSize: 5000,
            };

            const response = await exportTrigger(exportFilters).unwrap();
            const rows = response?.data ?? response ?? [];

            if (!Array.isArray(rows) || rows.length === 0) {
                openNotification("No data found to export.", "warning");
                return;
            }

            const isDateField = (field) => ["siteVisitDate", "startDate", "proposalDate", "loiDate"].includes(field);

            const formattedData = rows.map((row, index) => {
                const rowData = { "S.No": index + 1 };

                ALL_EXPORT_COLUMNS.forEach((col) => {
                    const rawValue = row?.[col.field];
                    let value = rawValue ?? "N/A";

                    if (isDateField(col.field) && rawValue && moment(rawValue).isValid()) {
                        value = moment.utc(rawValue).format("DD MMM YYYY");
                    }

                    rowData[col.name] = value;
                });

                return rowData;
            });

            exportToExcel(formattedData, "Leads", "Leads Tracker");
            openNotification("Excel exported successfully", "success");
        } catch (error) {
            openNotification("Failed to export leads. Please try again.", "error");
        }
    };

    useEffect(() => {
        trigger(filters);
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: 10,
            SearchTerm: "",
        };
        setFilters(resetFilters);
        trigger(resetFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value ?? null,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const updatedFilters = {
            ...filters,
            [name]: value ? `${value}T00:00:00Z` : "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };

    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };

    const handleAddLeads = () => {
        setIsAddLeads((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditLeads = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditLeadsClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const handleDeleteClick = (row) => {
        setIsDelete((prev) => !prev);
        setDeleteData(row);
    };

    const handleDeleteClose = () => {
        setIsDelete(false);
        setDeleteData(null);
    };

    const handleMenuDelete = () => {
        if (menuRow) {
            handleDeleteClick(menuRow);
        }
        handleMenuClose();
    };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const numericRenderer = (field) => (row) => {
        const value = row?.[field];

        if (!value && value !== 0) return "N/A";

        return Number(value).toLocaleString("en-IN");
    };

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess || deleteAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                </>
            ),
        },
        {
            name: "ID",
            field: "customId",
            width: "100px",
            sticky: true,
        },
        {
            name: "Name",
            field: "name",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.name ? highlightText(row.name, searchTerm) : "N/A",
        },
        {
            name: "Asset Name",
            field: "assetName",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.assetName ? highlightText(row.assetName, searchTerm) : "N/A",
        },
        {
            name: "Asset Type",
            field: "assetType",
            width: "250px",
            renderer: (row) => row?.assetType ? highlightText(row.assetType, searchTerm) : "N/A",
        },
        {
            name: "Area In Sqft",
            field: "area",
            width: "200px",
            renderer: numericRenderer("area"),
        },
        {
            name: "Expectation Rental Rate",
            field: "expectationRentalRate",
            renderer: numericRenderer("expectationRentalRate"),
        },
        {
            name: "Status",
            field: "status",
            renderer: (row) => (
                row?.status ? <StatusBadge status={row?.status} /> : "N/A"
            )
        },
        {
            name: "Type",
            field: "type",
            renderer: (row) => row?.type ? highlightText(row.type, searchTerm) : "N/A",
        },
        {
            name: "Site Visit Date",
            field: "siteVisitDate",
            renderer: (row) => (
                row?.siteVisitDate ? moment.utc(row.siteVisitDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Start Date",
            field: "startDate",
            renderer: (row) => (
                row?.startDate ? moment.utc(row.startDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Source",
            field: "source",
            renderer: (row) => row?.source ? row?.source : "N/A",
        },
        {
            name: "Proposal Status",
            field: "proposalStatus",
            renderer: (row) => (
                row?.proposalStatus ? <StatusBadge status={row?.proposalStatus} /> : "N/A"
            )
        },
        {
            name: "Proposal Date",
            field: "proposalDate",
            renderer: (row) => (
                row?.proposalDate ? moment.utc(row.proposalDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Proposed Rental Base",
            field: "proposedRentalBase",
            renderer: numericRenderer("proposedRentalBase"),
        },
        {
            name: "Proposed Rental CAM",
            field: "proposedRentalCam",
            renderer: numericRenderer("proposedRentalCam"),
        },
        {
            name: "Proposed Rental TI",
            field: "proposedRentalTi",
            renderer: numericRenderer("proposedRentalTi"),
        },
        {
            name: "Current Stage",
            field: "currentStage",
            renderer: (row) => row?.currentStage ? highlightText(row.currentStage, searchTerm) : "N/A",
        },
        {
            name: "LOI Date",
            field: "loiDate",
            renderer: (row) => (
                row?.loiDate ? moment.utc(row.loiDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Time To Deal",
            field: "timeToDeal",
            renderer: (row) => row?.timeToDeal ?? "N/A",
        },
        {
            name: "Last Action",
            field: "lastAction",
            width: "300px",
            renderer: (row) => row?.lastAction ? highlightText(row.lastAction, searchTerm) : "N/A",
        },
        {
            name: "Next Steps",
            field: "nextSteps",
            width: "300px",
            renderer: (row) => row?.nextSteps ? highlightText(row.nextSteps, searchTerm) : "N/A",
        },
    ];

    return (
        <>
            <TrackerPageHeader title="Leads Tracker" description="Manage leasing leads, stages, proposals, and follow-ups.">
                <ProjectLastUpdatedOn date={leadsData?.leads} />
            </TrackerPageHeader>
            <Grid container spacing={2} sx={{ p: 2 }}>

                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: "2px solid #ECECEC",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            //onChange={handleTabChange}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {["All"].map((label, index) => {
                                let count = 0;

                                if (label === "All") {
                                    count = totalCount;
                                }
                                else if (label === "Open") {
                                    count = openCount;
                                }
                                else if (label === "Closed") {
                                    count = closedCount;
                                }

                                return (
                                    <Tab
                                        key={index}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                        fontFamily: "Outfit, sans-serif"
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                {index > 0 &&
                                                    <Chip
                                                        label={count}
                                                        sx={{
                                                            height: "20px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            backgroundColor: "#E5E9EB",
                                                            color: "#5F5F5F",
                                                        }}
                                                    />
                                                }

                                            </Box>
                                        }
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "auto",
                                            paddingBottom: "14px",
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {createAccess &&
                            <CustomButton
                                label="Create Lead"
                                startIcon={<FaPlus />}
                                color="primary"
                                onClick={handleAddLeads}
                                sx={{
                                    backgroundColor: "#2563EB",
                                    fontFamily: 'Outfit',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '100%',
                                    letterSpacing: '0px',
                                }}
                            />
                        }
                    </Box>
                </Grid>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >

                                {filters?.Type &&
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Type:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>{filters?.Type}</span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Type')}
                                    />
                                }

                                {filters.StartDateTo && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Start Date To:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.StartDateTo).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('StartDateTo')}
                                    />
                                )}

                                {filters.StartDateFrom && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Start Date From:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.StartDateFrom).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('StartDateFrom')}
                                    />
                                )}

                                {filters.Status && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Status:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Status}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Status')}
                                    />
                                )}

                                {filters.ProposalStatus && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Proposal Status:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.ProposalStatus}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('ProposalStatus')}
                                    />
                                )}
                                {filters.CurrentStage && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Current Stage:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.CurrentStage}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('CurrentStage')}
                                    />
                                )}

                            </Stack>
                        </Grid>


                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By Name, Asset Name, Asset Type ..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '320px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', minHeight: '36px', fontSize: '13px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton>

                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Lead Status</Typography>
                                                                <Select
                                                                    name="Status"
                                                                    options={statusOptions}
                                                                    isClearable
                                                                    placeholder="Select Lead Status"
                                                                    onChange={(option) => handleSelectChange(option, "Status")}
                                                                    value={statusOptions.find(option => option.value === filters.Status) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Lead Type</Typography>
                                                                <Select
                                                                    name="Type"
                                                                    options={typeOptions}
                                                                    isClearable
                                                                    placeholder="Select Lead Type"
                                                                    onChange={(option) => handleSelectChange(option, "Type")}
                                                                    value={typeOptions.find(option => option.value === filters.Type) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Proposal Status</Typography>
                                                                <Select
                                                                    name="ProposalStatus"
                                                                    options={proposalStatusOptions}
                                                                    isClearable
                                                                    placeholder="Select Proposal Status"
                                                                    onChange={(option) => handleSelectChange(option, "ProposalStatus")}
                                                                    value={proposalStatusOptions.find(option => option.value === filters.ProposalStatus) || null}
                                                                />
                                                            </Grid>
                                                             <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Current Stage</Typography>
                                                                <Select
                                                                    name="CurrentStage"
                                                                    options={currentStageOptions}
                                                                    isClearable
                                                                    placeholder="Select Current Stage"
                                                                    onChange={(option) => handleSelectChange(option, "CurrentStage")}
                                                                    value={currentStageOptions.find(option => option.value === filters.CurrentStage) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Start Date To</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="StartDateTo"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.StartDateTo ? new Date(filters.StartDateTo).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Start Date From</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="StartDateFrom"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.StartDateFrom ? new Date(filters.StartDateFrom).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "flex-end",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Button onClick={handleReset}
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            color: "#F15744",
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            borderRadius: "3px",
                                                                            fontWeight: 500,
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}>
                                                                        Reset
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleApplyFilters}
                                                                        variant="contained"
                                                                        sx={{
                                                                            backgroundColor: "#2563EB",
                                                                            borderRadius: "3px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={() => {
                                    trigger(filters);
                                }}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>

                                <IconButton onClick={handleExportExcel} disabled={isExporting}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12}>
                    <TableComponent
                        data={leadsList?.data || []}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        {viewAccess &&
                            <MenuItem
                                onClick={handleMenuView}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <LuEye size={16} style={{ color: "#4B5563" }} />
                                <span>View</span>
                            </MenuItem>
                        }

                        {editAccess &&
                            <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem>
                        }

                        {deleteAccess &&
                            <MenuItem
                                onClick={handleMenuDelete}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <MdDeleteOutline style={{ width: 14, height: 14 }} />
                                <span>Delete</span>
                            </MenuItem>
                        }
                    </Menu>

                    <CustomPagination
                        totalCount={leadsList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />

                </Grid>
            </Grid >
            {isAddLeads && <AddLeads open={isAddLeads} handleClose={handleAddLeads} typeOptions={typeOptions} statusOptions={statusOptions} currentStageOptions={currentStageOptions} proposalStatusOptions={proposalStatusOptions} />}
            {isEdit && <EditLeads open={isEdit} handleClose={handleEditLeadsClose} rowData={selectedData} typeOptions={typeOptions} currentStageOptions={currentStageOptions} statusOptions={statusOptions} proposalStatusOptions={proposalStatusOptions} />}
            {isView && <ViewLeads open={isView} handleClose={handleViewClose} rowData={viewData} />}
            {isDelete && <DeleteLeads open={isDelete} handleClose={handleDeleteClose} rowData={deleteData} />}
        </>
    );
}

export default LeadsTracker;
